import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { ArrowLeft, ChevronRight, BadgeCheck, AlertCircle, Trash2, Loader2, Mail } from "lucide-react";
import api, { fmtError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { DeleteAccountModal } from "./Settings";

/**
 * Settings → Account — Yubo-style account information screen.
 * Read-only rows (label left, value right) with chevrons where an action exists.
 */
export default function SettingsAccount() {
  const { t, i18n } = useTranslation();
  const nav = useNavigate();
  const { user, setUser, logout, refresh } = useAuth();
  const [showDelete, setShowDelete] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [showUsernameModal, setShowUsernameModal] = useState(false);
  const [showGenderModal, setShowGenderModal] = useState(false);
  const [showAgeModal, setShowAgeModal] = useState(false);

  useEffect(() => {
    refresh();
  }, [refresh]);

  if (!user) return null;

  // All accounts are now Clerk-managed. Email + password changes happen
  // through Clerk's own UI — we send users back to /auth for those actions.
  const isClerkUser = user.auth_provider === "clerk" || user.clerk_id;
  const genderLabel =
    { male: t("createActivity.male"), man: t("createActivity.male"),
      female: t("createActivity.female"), woman: t("createActivity.female"),
      other: t("createActivity.other") }[(user.gender || "").toLowerCase()] || user.gender || "—";
  const memberSince = user.created_at
    ? new Date(user.created_at).toLocaleDateString(i18n.language, { year: "numeric", month: "long", day: "numeric" })
    : "—";

  const rows = [
    { key: "avatar", label: "Profile Picture", value: user.avatar_url, onClick: () => nav("/profile/edit") },
    { key: "name", label: t("settings.accName"), value: user.name, onClick: () => setShowNameModal(true) },
    { key: "username", label: t("settings.accUsername"), value: user.username ? `@${user.username}` : "—", onClick: () => setShowUsernameModal(true) },
    { key: "age", label: t("settings.accAge"), value: user.age ?? "—", onClick: () => setShowAgeModal(true) },
    { key: "gender", label: t("settings.accGender"), value: genderLabel, onClick: () => setShowGenderModal(true) },
    {
      key: "email", label: t("settings.accEmail"), value: user.email,
      badge: user.email_verified
        ? { icon: BadgeCheck, text: t("settings.accVerified"), color: "#0F9D8F" }
        : { icon: AlertCircle, text: t("settings.accNotVerified"), color: "#D98324" },
      // Email changes are managed via Clerk — show a toast explaining this
      onClick: isClerkUser
        ? () => toast.message("Email changes are managed through your Clerk account settings.")
        : () => setShowEmailModal(true),
    },
    {
      key: "password", label: t("settings.accPassword"),
      value: "Managed by Clerk",
      // Clerk handles password resets via the /auth SignIn screen
      onClick: () => toast.message("To change your password, sign out and use 'Forgot password?' on the login page."),
    },
    {
      key: "idverify", label: t("settings.accIdVerification"),
      value: "",
      badge: user.verified
        ? { icon: BadgeCheck, text: t("settings.accVerified"), color: "#0F9D8F" }
        : { icon: AlertCircle, text: t("settings.accNotVerified"), color: "#D98324" },
      onClick: user.verified ? undefined : () => nav("/profile"),
    },
    { key: "since", label: t("settings.accMemberSince"), value: memberSince },
  ];

  return (
    <div className="min-h-screen bg-white text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="settings-account-page">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-[#E2DFD4]">
        <div className="flex items-center gap-2 px-5 py-5">
          <button
            onClick={() => nav(-1)}
            className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-[#F4F1E8]"
            data-testid="account-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-7 h-7" strokeWidth={3} />
          </button>
          <h1 className="font-display font-black text-[34px] tracking-tight">{t("settings.account")}</h1>
        </div>
      </div>

      {/* Info rows */}
      <ul className="divide-y divide-[#F0EDE3]">
        {rows.map((r) => {
          const Row = r.onClick ? "button" : "div";
          return (
            <li key={r.key}>
              <Row
                onClick={r.onClick}
                className={`w-full flex items-center gap-3 px-5 py-5 text-left ${r.onClick ? "hover:bg-[#FAF8F2] active:bg-[#F4F1E8] transition-colors" : ""}`}
                data-testid={`account-row-${r.key}`}
              >
                <span className="font-display font-semibold text-lg flex-1">{r.label}</span>
                {r.badge && (
                  <span className="flex items-center gap-1 text-sm font-bold" style={{ color: r.badge.color }}>
                    <r.badge.icon className="w-4 h-4" strokeWidth={2.5} /> {r.badge.text}
                  </span>
                )}
                {r.key === "avatar" ? (
                  <div className="w-12 h-12 rounded-full overflow-hidden border border-[#0A0B0D] shadow-nb-sm shrink-0 bg-[#F4F1E8] flex items-center justify-center font-display font-bold">
                    {user.avatar_url ? (
                      <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                    ) : (
                      (user.name?.[0] || "?").toUpperCase()
                    )}
                  </div>
                ) : r.value ? (
                  <span className="text-[#7C828D] text-base truncate max-w-[45%]">{r.value}</span>
                ) : null}
                {r.onClick && <ChevronRight className="w-5 h-5 text-[#B9BDC7] shrink-0" strokeWidth={2.5} />}
              </Row>
            </li>
          );
        })}
      </ul>

      <div className="h-3 bg-[#F4F1E8]" aria-hidden="true" />

      {/* Danger zone — delete account (Yubo keeps this under Account) */}
      <button
        onClick={() => setShowDelete(true)}
        className="w-full flex items-center gap-4 px-5 py-5 text-left hover:bg-[#FFF5F3] active:bg-[#FFE6E0] transition-colors"
        data-testid="account-delete"
      >
        <Trash2 className="w-6 h-6 text-[#D14848]" strokeWidth={2} />
        <span className="font-display text-lg text-[#D14848] font-bold">{t("settings.deleteAccount")}</span>
      </button>

      {showNameModal && (
        <NameModal
          t={t}
          current={user.name}
          onClose={() => setShowNameModal(false)}
          onSaved={async (newName) => { 
            setUser({ ...user, name: newName });
            setShowNameModal(false); 
            await refresh(); 
          }}
        />
      )}

      {showUsernameModal && (
        <UsernameModal
          t={t}
          current={user.username}
          onClose={() => setShowUsernameModal(false)}
          onSaved={async (newUsername) => {
            setUser({ ...user, username: newUsername });
            setShowUsernameModal(false);
            await refresh();
          }}
        />
      )}

      {showGenderModal && (
        <GenderModal
          t={t}
          current={(user.gender || "").toLowerCase()}
          onClose={() => setShowGenderModal(false)}
          onSaved={async () => { setShowGenderModal(false); await refresh(); }}
        />
      )}

      {showAgeModal && (
        <AgeModal
          t={t}
          current={user.age}
          onClose={() => setShowAgeModal(false)}
          onSaved={async () => { setShowAgeModal(false); await refresh(); }}
        />
      )}

      {showEmailModal && (
        <ChangeEmailModal
          t={t}
          onClose={() => setShowEmailModal(false)}
          onChanged={async () => { setShowEmailModal(false); await refresh(); }}
        />
      )}

      {showDelete && (
        <DeleteAccountModal
          onClose={() => setShowDelete(false)}
          onConfirmed={async () => {
            setShowDelete(false);
            await logout();
            nav("/", { replace: true });
          }}
        />
      )}
    </div>
  );
}

/** Pick gender: female / male / other — saved via PATCH /users/me. */
function GenderModal({ t, current, onClose, onSaved }) {
  const [val, setVal] = useState(["male", "female", "other"].includes(current) ? current : "");
  const [busy, setBusy] = useState(false);
  const opts = [
    { v: "female", label: t("createActivity.female") },
    { v: "male", label: t("createActivity.male") },
    { v: "other", label: t("createActivity.other") },
  ];

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me", { gender: val });
      toast.success(t("settings.accGenderSaved"));
      onSaved();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="gender-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-16 sm:pb-6 shadow-2xl">
        <h2 className="font-display font-black text-xl mb-4">{t("settings.accGender")}</h2>
        <div className="space-y-2 mb-5">
          {opts.map((o) => (
            <button
              key={o.v}
              onClick={() => setVal(o.v)}
              className={`w-full flex items-center justify-between px-4 py-3.5 rounded-2xl font-bold text-left transition-colors ${
                val === o.v ? "bg-[#00CED1]/15 border-2 border-[#00CED1]" : "bg-[#F4F1E8] border-2 border-transparent hover:bg-[#E9E5D7]"
              }`}
              data-testid={`gender-option-${o.v}`}
            >
              {o.label}
              {val === o.v && <BadgeCheck className="w-5 h-5 text-[#00CED1]" strokeWidth={2.5} />}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} disabled={busy}
            className="flex-1 bg-[#F4F1E8] hover:bg-[#E9E5D7] text-black font-bold py-3 rounded-2xl"
            data-testid="gender-cancel">
            {t("common.cancel")}
          </button>
          <button onClick={save} disabled={busy || !val}
            className="flex-1 bg-[#00CED1] text-black font-black py-3 rounded-2xl border-2 border-[#121212] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="gender-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Two-step change-email flow: new email + password → 6-digit code sent to
 *  the NEW address → confirm. */
function ChangeEmailModal({ t, onClose, onChanged }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const errMsg = (detail) => {
    const c = typeof detail === "object" ? detail?.code : null;
    const map = {
      email_taken: t("settings.errEmailTaken"),
      wrong_password: t("settings.errWrongPassword"),
      same_email: t("settings.errSameEmail"),
      wrong_code: t("settings.errWrongCode"),
      code_expired: t("settings.errCodeExpired"),
      too_many_attempts: t("settings.errTooManyAttempts"),
      google_managed: t("settings.accEmailGoogle"),
    };
    return map[c] || fmtError(detail);
  };

  const requestCode = async () => {
    setBusy(true);
    try {
      await api.post("/auth/email/change-request", { new_email: email.trim(), password });
      toast.success(t("settings.accCodeSentTo", { email: email.trim() }));
      setStep(2);
    } catch (e) {
      toast.error(errMsg(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/auth/email/change-confirm", { code: code.trim() });
      toast.success(t("settings.accEmailChanged"));
      onChanged();
    } catch (e) {
      toast.error(errMsg(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="change-email-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-16 sm:pb-6 shadow-2xl">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-12 h-12 rounded-full bg-[#E6FBF9] flex items-center justify-center shrink-0">
            <Mail className="w-6 h-6 text-[#0F9D8F]" strokeWidth={2.5} />
          </div>
          <div>
            <h2 className="font-display font-black text-xl">{t("settings.accChangeEmailTitle")}</h2>
            <p className="text-sm text-[#3D424B] mt-1">
              {step === 1 ? t("settings.accChangeEmailBody") : t("settings.accCodeSentTo", { email: email.trim() })}
            </p>
          </div>
        </div>

        {step === 1 ? (
          <>
            <label className="block text-xs font-bold text-[#7C828D] uppercase tracking-wide mb-1.5">{t("settings.accNewEmail")}</label>
            <input
              type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus
              className="w-full bg-[#F4F1E8] rounded-2xl px-4 py-3 text-base placeholder-[#7C828D] outline-none focus:bg-[#E9E5D7] mb-3"
              placeholder="you@email.com" data-testid="change-email-input"
            />
            <label className="block text-xs font-bold text-[#7C828D] uppercase tracking-wide mb-1.5">{t("settings.accCurrentPassword")}</label>
            <input
              type="password" value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-[#F4F1E8] rounded-2xl px-4 py-3 text-base placeholder-[#7C828D] outline-none focus:bg-[#E9E5D7] mb-4"
              placeholder="••••••••" data-testid="change-email-password"
            />
          </>
        ) : (
          <>
            <label className="block text-xs font-bold text-[#7C828D] uppercase tracking-wide mb-1.5">{t("settings.accCodeLabel")}</label>
            <input
              type="text" inputMode="numeric" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} autoFocus
              className="w-full bg-[#F4F1E8] rounded-2xl px-4 py-3 text-2xl font-black tracking-[0.4em] text-center outline-none focus:bg-[#E9E5D7] mb-4"
              placeholder="000000" data-testid="change-email-code"
            />
          </>
        )}

        <div className="flex gap-2">
          <button onClick={onClose} disabled={busy}
            className="flex-1 bg-[#F4F1E8] hover:bg-[#E9E5D7] text-black font-bold py-3 rounded-2xl"
            data-testid="change-email-cancel">
            {t("common.cancel")}
          </button>
          <button
            onClick={step === 1 ? requestCode : confirm}
            disabled={busy || (step === 1 ? !email.trim() || !password : code.length !== 6)}
            className="flex-1 bg-[#00CED1] text-black font-black py-3 rounded-2xl border-2 border-[#121212] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="change-email-submit"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {step === 1 ? t("settings.accSendCode") : t("settings.accConfirm")}
          </button>
        </div>
      </div>
    </div>
  );
}

function AgeModal({ t, current, onClose, onSaved }) {
  const [val, setVal] = useState(current || 18);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me", { age: Number(val) });
      toast.success("Age updated successfully");
      onSaved();
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="age-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-16 sm:pb-6 shadow-2xl">
        <h2 className="font-display font-black text-xl mb-4">Edit Age</h2>
        <div className="space-y-4 mb-5">
          <input
            type="number"
            min={18}
            max={120}
            className="input-nb w-full py-3"
            value={val}
            onChange={(e) => setVal(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} disabled={busy}
            className="flex-1 bg-[#F4F1E8] hover:bg-[#E9E5D7] text-black font-bold py-3 rounded-2xl"
            data-testid="age-cancel">
            {t("common.cancel")}
          </button>
          <button onClick={save} disabled={busy || !val || val < 18}
            className="flex-1 bg-[#00CED1] text-black font-black py-3 rounded-2xl border-2 border-[#121212] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="age-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function NameModal({ t, current, onClose, onSaved }) {
  const [val, setVal] = useState(current || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me", { name: val });
      toast.success(t("settings.accNameSaved") || "Name updated successfully");
      onSaved(val);
    } catch (e) {
      toast.error(fmtError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="name-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-16 sm:pb-6 shadow-2xl">
        <h2 className="font-display font-black text-xl mb-4">{t("settings.accName")}</h2>
        <div className="space-y-4 mb-5">
          <input
            type="text"
            maxLength={80}
            className="w-full bg-[#F4F1E8] rounded-2xl px-4 py-3 text-base placeholder-[#7C828D] outline-none focus:bg-[#E9E5D7]"
            value={val}
            onChange={(e) => setVal(e.target.value)}
            autoFocus
          />
        </div>
        <div className="flex gap-2">
          <button onClick={onClose} disabled={busy}
            className="flex-1 bg-[#F4F1E8] hover:bg-[#E9E5D7] text-black font-bold py-3 rounded-2xl"
            data-testid="name-cancel">
            {t("common.cancel")}
          </button>
          <button onClick={save} disabled={busy || !val.trim() || val.length < 2}
            className="flex-1 bg-[#00CED1] text-black font-black py-3 rounded-2xl border-2 border-[#121212] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="name-save">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

function UsernameModal({ t, current, onClose, onSaved }) {
  const [val, setVal] = useState(current || "");
  const [busy, setBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const save = async () => {
    const clean = val.trim().toLowerCase().replace(/^@/, "");
    if (!clean || clean.length < 3 || clean.length > 30) {
      setErrorMsg("Username must be between 3 and 30 characters.");
      return;
    }
    if (!/^[a-zA-Z0-9._]+$/.test(clean)) {
      setErrorMsg("Username can only contain letters, numbers, underscores, and periods.");
      return;
    }
    setBusy(true);
    setErrorMsg("");
    try {
      await api.patch("/users/me", { username: clean });
      toast.success(t("settings.accUsernameSaved") || "Username updated successfully!");
      onSaved(clean);
    } catch (e) {
      const msg = fmtError(e.response?.data?.detail) || e.message;
      setErrorMsg(msg);
      toast.error(msg);
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[80] bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="username-modal">
      <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-6 pb-16 sm:pb-6 shadow-2xl">
        <h2 className="font-display font-black text-xl mb-1">{t("settings.accUsername")}</h2>
        <p className="text-xs text-[#7C828D] mb-4">{t("settings.accUsernameHint") || "3-30 characters long. Letters, numbers, underscores, and periods allowed."}</p>

        <div className="space-y-3 mb-5">
          <div className="relative flex items-center">
            <span className="absolute left-4 font-bold text-[#7C828D] text-base select-none">@</span>
            <input
              type="text"
              maxLength={30}
              className="w-full bg-[#F4F1E8] rounded-2xl pl-9 pr-4 py-3 text-base placeholder-[#7C828D] outline-none focus:bg-[#E9E5D7] font-semibold"
              value={val}
              onChange={(e) => {
                setVal(e.target.value.toLowerCase().replace(/[^a-zA-Z0-9._]/g, ""));
                setErrorMsg("");
              }}
              placeholder="username"
              data-testid="username-input"
              autoFocus
            />
          </div>
          {errorMsg && (
            <p className="text-xs font-bold text-[#D14848] px-1" data-testid="username-error">
              {errorMsg}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={onClose}
            disabled={busy}
            className="flex-1 bg-[#F4F1E8] hover:bg-[#E9E5D7] text-black font-bold py-3 rounded-2xl"
            data-testid="username-cancel"
          >
            {t("common.cancel")}
          </button>
          <button
            onClick={save}
            disabled={busy || !val.trim() || val.trim().length < 3}
            className="flex-1 bg-[#00CED1] text-black font-black py-3 rounded-2xl border-2 border-[#121212] disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="username-save"
          >
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}
            {t("common.save")}
          </button>
        </div>
      </div>
    </div>
  );
}

